<template>
  <div class="bracket-progress" v-if="nextBracket">
    <div class="bracket-header">
      <span class="bracket-title">Next Bracket</span>
      <i class="fa-regular fa-arrow-up bracket-arrow"></i>
    </div>
    <p class="bracket-subtitle">
      {{ lotsRemaining }} lots to reach the {{ nextBracket.bracketLabel }}
    </p>
    <div class="bracket-bar">
      <div class="bracket-bar-fill" :style="{ width: progressPercent + '%' }"></div>
    </div>
    <div class="bracket-caption">
      <span class="bracket-current">Current Lots: {{ currentLots }}</span>
      <span class="bracket-target">{{ targetLots }}</span>
    </div>
  </div>
</template>

<script>
import TournamentRepository from '@/repositories/tournament'

export default {
  name: 'BracketProgress',

  props: {
    scoreBracket: {
      type: Number,
      default: 0
    },
    tournamentId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      brackets: []
    }
  },

  computed: {
    currentLots() {
      return Number(this.scoreBracket || 0).toFixed(2)
    },

    sortedBrackets() {
      if (!this.brackets.length) return []
      return [...this.brackets].sort((a, b) => {
        const aVal = this.extractBracketMax(a.bracketLabel)
        const bVal = this.extractBracketMax(b.bracketLabel)
        return aVal - bVal
      })
    },

    nextBracket() {
      const current = this.scoreBracket || 0
      return this.sortedBrackets.find(b => {
        const max = this.extractBracketMax(b.bracketLabel)
        return max > current
      }) || null
    },

    currentBracketMin() {
      if (!this.nextBracket) return 0
      const idx = this.sortedBrackets.indexOf(this.nextBracket)
      if (idx <= 0) return 0
      return this.extractBracketMax(this.sortedBrackets[idx - 1].bracketLabel)
    },

    targetLots() {
      if (!this.nextBracket) return 0
      return this.extractBracketMax(this.nextBracket.bracketLabel)
    },

    lotsRemaining() {
      const remaining = this.targetLots - (this.scoreBracket || 0)
      return Math.max(0, remaining).toFixed(2)
    },

    progressPercent() {
      if (!this.targetLots) return 0
      const range = this.targetLots - this.currentBracketMin
      if (range <= 0) return 100
      const progress = ((this.scoreBracket || 0) - this.currentBracketMin) / range * 100
      return Math.min(100, Math.max(0, progress))
    }
  },

  watch: {
    tournamentId: {
      immediate: true,
      handler(id) {
        if (id) this.fetchBrackets(id)
      }
    }
  },

  methods: {
    extractBracketMax(label) {
      // Bracket labels like "0-10", "10-50", "50-100"
      // Extract the upper bound number
      const parts = String(label).split('-').map(s => parseFloat(s.trim()))
      return parts.length > 1 ? parts[1] : parts[0] || 0
    },

    async fetchBrackets(tournamentId) {
      try {
        const response = await TournamentRepository.getTournamentBrackets(tournamentId)
        if (response.status === 200 && response.data && response.data.length > 0) {
          this.brackets = response.data
        }
      } catch {
        // No brackets - component stays hidden
        this.brackets = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.bracket-progress {
  border: 1px solid #ABABAB;
  border-radius: 10px;
  padding: 20px;
  margin: 0 24px 12px;
}

.bracket-header {
  display: flex;
  align-items: center;
  gap: 5px;
}

.bracket-title {
  font-family: 'Avenir', sans-serif;
  font-weight: 800;
  font-size: 14px;
  line-height: 1.5;
  color: var(--v-mainBlack-base, #14161B);
}

.bracket-arrow {
  font-size: 10px;
  color: #333;
}

.bracket-subtitle {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: var(--v-mainBlack-base, #14161B);
  margin: 0 0 10px;
  text-align: left;
}

.bracket-bar {
  width: 100%;
  height: 8px;
  background-color: #EBEBEB;
  border-radius: 16px;
  overflow: hidden;
}

.bracket-bar-fill {
  height: 100%;
  background-color: #0064FA;
  border-radius: 16px;
  transition: width 0.4s ease;
  min-width: 0;
}

.bracket-caption {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
}

.bracket-current {
  font-family: 'Avenir', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #2196F3;
}

.bracket-target {
  font-family: 'Avenir', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: 0.28px;
  color: #ABABAB;
  text-align: right;
}

/* Mobile */
@media (max-width: 600px) {
  .bracket-progress {
    margin: 0 15px 10px;
    padding: 15px;
  }
}
</style>
